// Audit Log admin page
// Search and browse all audit history across the system
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import { apiRequest } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useAssignableUsers, useUserLookup } from '../../hooks/useUsers'
import { Avatar } from '../../components/ui/Avatar'

interface AuditLog {
  id: string
  entity: string
  entityId: string
  action: 'create' | 'update' | 'delete'
  userId: string
  timestamp: string
  oldValues: Record<string, unknown> | null
  newValues: Record<string, unknown> | null
  changedFields: string[] | null
}

interface AuditResponse {
  data: AuditLog[]
  total: number
  page: number
  limit: number
  totalPages: number
}

const ENTITIES = ['leads', 'accounts', 'contacts', 'opportunities', 'events', 'notes'] as const
const ACTIONS = ['create', 'update', 'delete'] as const
const EXCLUDED_FIELDS = ['id', 'createdAt', 'updatedAt', 'ownerId']

export function AuditLogPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { getUser } = useUserLookup()
  const [entityFilter, setEntityFilter] = useState<string>('')
  const [actionFilter, setActionFilter] = useState<string>('')
  const [userFilter, setUserFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set())
  const limit = 25

  const { data: usersData } = useAssignableUsers()
  const users = usersData?.data || []

  // Build query params
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('limit', String(limit))
  if (entityFilter) params.set('entity', entityFilter)
  if (actionFilter) params.set('action', actionFilter)
  if (userFilter) params.set('userId', userFilter)

  const { data: auditData, isLoading } = useQuery({
    queryKey: ['audit', 'all', entityFilter, actionFilter, userFilter, page],
    queryFn: () => apiRequest<AuditResponse>(`/audit?${params.toString()}`),
  })

  const logs = auditData?.data || []
  const total = auditData?.total || 0
  const totalPages = auditData?.totalPages || 1

  const canManage = user?.role === 'admin'

  const toggleExpanded = (logId: string) => {
    setExpandedLogs(prev => {
      const next = new Set(prev)
      if (next.has(logId)) {
        next.delete(logId)
      } else {
        next.add(logId)
      }
      return next
    })
  }

  const getEntityLink = (entity: string, entityId: string) => {
    return `/${entity}/${entityId}`
  }

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'create': return t('audit.created')
      case 'update': return t('audit.updated')
      case 'delete': return t('audit.deleted')
      default: return action
    }
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create': return 'bg-green-100 text-green-700'
      case 'update': return 'bg-blue-100 text-blue-700'
      case 'delete': return 'bg-red-100 text-red-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  const formatFieldName = (field: string): string => {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim()
  }

  const formatUserName = (user?: { firstName?: string | null; lastName?: string | null; email: string } | null) => {
    if (!user) return t('audit.unknownUser')
    return user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email
  }

  const renderChanges = (log: AuditLog) => {
    if (log.action === 'create') {
      const values = log.newValues || {}
      const fields = Object.entries(values).filter(
        ([key]) => !EXCLUDED_FIELDS.includes(key) && values[key] !== null && values[key] !== ''
      )
      if (fields.length === 0) return <p className="text-sm text-gray-500">No visible fields</p>
      return (
        <div className="space-y-1">
          {fields.map(([key, value]) => (
            <div key={key} className="text-sm">
              <span className="font-medium text-gray-600">{formatFieldName(key)}:</span>{' '}
              <span className="text-gray-900">{formatValue(value)}</span>
            </div>
          ))}
        </div>
      )
    }

    if (log.action === 'update' && log.changedFields && log.changedFields.length > 0) {
      const changes = log.changedFields.filter(f => !EXCLUDED_FIELDS.includes(f))
      if (changes.length === 0) return <p className="text-sm text-gray-500">No visible field changes</p>
      return (
        <div className="space-y-1">
          {changes.map((field) => {
            const oldVal = log.oldValues?.[field]
            const newVal = log.newValues?.[field]
            return (
              <div key={field} className="text-sm">
                <span className="font-medium text-gray-600">{formatFieldName(field)}:</span>{' '}
                <span className="text-red-600 line-through">{formatValue(oldVal)}</span>
                {' → '}
                <span className="text-green-600">{formatValue(newVal)}</span>
              </div>
            )
          })}
        </div>
      )
    }

    if (log.action === 'delete') {
      return <p className="text-sm text-gray-500">Record deleted</p>
    }

    return null
  }

  if (!canManage) {
    return (
      <>
        <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('nav.auditLog')}</h2>
              <p className="mt-1 text-sm text-gray-500">{t('pages.auditLog.subtitle')}</p>
            </div>
          </div>
        </aside>
        <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
          <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
            <div className="text-center py-12">
              <h2 className="text-lg font-medium text-gray-900">{t('common.accessDenied')}</h2>
              <p className="mt-2 text-sm text-gray-500">{t('users.adminOnly')}</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (isLoading) {
    return (
      <>
        <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-3/4" />
          </div>
        </aside>
        <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
          <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] flex items-center justify-center">
            <div className="text-gray-500">{t('common.loading')}</div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Secondary column - Filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('nav.auditLog')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('pages.auditLog.subtitle')}</p>
          </div>

          {/* Entity Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('sharingRules.filterByEntity')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setEntityFilter(''); setPage(1) }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  entityFilter === ''
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {ENTITIES.map(entity => (
                <button
                  key={entity}
                  onClick={() => { setEntityFilter(entity); setPage(1) }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    entityFilter === entity
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(`nav.${entity}` as any)}
                </button>
              ))}
            </div>
          </div>

          {/* Action Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')} {t('fields.action')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setActionFilter(''); setPage(1) }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  actionFilter === ''
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {ACTIONS.map(action => (
                <button
                  key={action}
                  onClick={() => { setActionFilter(action); setPage(1) }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    actionFilter === action
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {getActionLabel(action)}
                </button>
              ))}
            </div>
          </div>

          {/* User Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')} {t('fields.user')}</h3>
            <select
              value={userFilter}
              onChange={(e) => { setUserFilter(e.target.value); setPage(1) }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
            >
              <option value="">{t('common.all')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {formatUserName(user)}
                </option>
              ))}
            </select>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.leads.stats.title')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.leads.stats.total')}</dt>
                <dd className="font-medium text-gray-900">{total}</dd>
              </div>
              {(entityFilter || actionFilter || userFilter) && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">{t('common.filtered')}</dt>
                  <dd className="font-medium text-gray-900">{logs.length}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('nav.auditLog')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('pages.auditLog.subtitle')}</p>

            {/* Mobile filters */}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => { setEntityFilter(''); setPage(1) }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  entityFilter === ''
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {ENTITIES.map(entity => (
                <button
                  key={entity}
                  onClick={() => { setEntityFilter(entity); setPage(1) }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    entityFilter === entity
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(`nav.${entity}` as any)}
                </button>
              ))}
            </div>
          </div>

          {/* Audit Log List */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  {logs.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-sm text-gray-500">{t('pages.auditLog.noLogs')}</p>
                    </div>
                  ) : (
                    <>
                      <div className="divide-y divide-gray-200">
                        {logs.map((log) => {
                          const actor = getUser(log.userId)
                          const isExpanded = expandedLogs.has(log.id)
                          const hasDetails = log.action === 'create' || (log.changedFields && log.changedFields.length > 0) || log.action === 'delete'

                          return (
                            <div key={log.id} className="p-4 hover:bg-gray-50 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <Avatar
                                    firstName={actor?.firstName || '?'}
                                    lastName={actor?.lastName}
                                    size="sm"
                                  />
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-medium text-gray-900">
                                        {formatUserName(actor)}
                                      </span>
                                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getActionColor(log.action)}`}>
                                        {getActionLabel(log.action)}
                                      </span>
                                      <Link
                                        to={getEntityLink(log.entity, log.entityId)}
                                        className="text-brand-600 hover:text-brand-900 text-sm"
                                      >
                                        <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                                          {t(`nav.${log.entity}` as any)}
                                        </span>
                                        <span className="text-gray-400 ml-1">#{log.entityId.slice(0, 8)}</span>
                                      </Link>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                      {new Date(log.timestamp).toLocaleString()}
                                    </p>

                                    {isExpanded && (
                                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                        {renderChanges(log)}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {hasDetails && (
                                  <button
                                    onClick={() => toggleExpanded(log.id)}
                                    className="text-gray-400 hover:text-gray-600 p-1"
                                  >
                                    {isExpanded ? (
                                      <ChevronUpIcon className="h-5 w-5" />
                                    ) : (
                                      <ChevronDownIcon className="h-5 w-5" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                          <p className="text-sm text-gray-700">
                            {t('common.showingOf', { count: logs.length, total })}
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setPage(p => Math.max(1, p - 1))}
                              disabled={page === 1}
                              className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 hover:bg-gray-50"
                            >
                              {t('common.previous')}
                            </button>
                            <span className="px-3 py-1 text-sm">
                              {page} / {totalPages}
                            </span>
                            <button
                              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                              disabled={page === totalPages}
                              className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 hover:bg-gray-50"
                            >
                              {t('common.next')}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
